import {
	Inter,
	Poppins,
	Bebas_Neue,
	Oswald,
	Open_Sans,
	Roboto,
	Libre_Baskerville,
} from 'next/font/google';

/**
 * Site-wide families, exposed to CSS as custom properties in `pages/_app.tsx`.
 * `weight` is omitted for variable families so one file covers the whole axis.
 *
 * Everything here routes through `_app`, so `preload` is all-or-nothing per family — there is
 * no per-page granularity. Preload the families in the global chrome; leave the rest to `swap`.
 */
export const oswald = Oswald({
	subsets: ['latin'],
	display: 'swap',
	// next/font's generated fallback is built over Arial, ~19% narrow for a condensed face, so
	// headings visibly resize on swap. Hence the tuned family in globals.css instead.
	adjustFontFallback: false,
	fallback: ['Oswald Fallback', 'Arial Narrow', 'Arial', 'sans-serif'],
});

export const openSans = Open_Sans({
	subsets: ['latin'],
	display: 'swap',
});

// Separate call so only the upright face is preloaded — `preload` has no per-style granularity.
export const openSansItalic = Open_Sans({
	subsets: ['latin'],
	style: ['italic'],
	display: 'swap',
	preload: false,
});

// On every page via the Footer, plus CalloutBox and BragBarV2 on the newer designs — so it's
// global chrome in practice and worth preloading.
export const roboto = Roboto({
	subsets: ['latin'],
	style: ['normal'],
	display: 'swap',
	preload: true,
});

// Split so the preload above covers only the upright face — nothing renders this in italic.
export const robotoItalic = Roboto({
	subsets: ['latin'],
	style: ['italic'],
	display: 'swap',
	preload: false,
});

/**
 * Only 400 and 700 are declarable — the upstream family is variable across 400–700, but
 * `next/font`'s bundled metadata still treats it as static.
 *
 * Preloaded even though one component uses it: embedded third-party content renders this family
 * too, with no fallback chain, so a face that isn't already in memory when it paints lands on the
 * browser's default serif.
 */
export const libreBaskerville = Libre_Baskerville({
	subsets: ['latin'],
	weight: ['400', '700'],
	style: ['normal'],
	display: 'swap',
	preload: true,
});

// Split so the preload above covers only the upright faces — nothing renders this family in
// italic, so preloading it would double the cost for no benefit.
export const libreBaskervilleItalic = Libre_Baskerville({
	subsets: ['latin'],
	weight: ['400', '700'],
	style: ['italic'],
	display: 'swap',
	preload: false,
});

/**
 * Declared as a variable face, so a single file covers every upright weight. Embedded third-party
 * content that asks for any of them finds the family already declared and skips fetching its own
 * copy — a per-weight declaration would only cover the exact weights listed.
 *
 * Preloaded because no markup of ours renders this family early enough to warm it otherwise.
 */
export const inter = Inter({
	subsets: ['latin'],
	style: ['normal'],
	variable: '--font-inter',
	display: 'swap',
	preload: true,
});

// Nothing renders this in italic, so it's declared but not preloaded. Dropping it entirely is what
// makes third-party content fetch a full italic weight ladder of its own, so it stays.
export const interItalic = Inter({
	subsets: ['latin'],
	style: ['italic'],
	variable: '--font-inter-italic',
	display: 'swap',
	preload: false,
});

/**
 * Listing a loader here is what keeps it alive — exporting alone lets next/font tree-shake it
 * and its @font-face rules silently never ship. Values also carry the `X Fallback` metric
 * family that globals.css's literal-name fallbacks don't.
 */
export const fontVars = [
	['--font-oswald', oswald],
	['--font-open-sans', openSans],
	['--font-roboto', roboto],
	['--font-libre-baskerville', libreBaskerville],
	// `-ref` = ballast, not a styling hook: `font-style` picks italic, so the value is the
	// upright token's.
	['--font-open-sans-italic-ref', openSansItalic],
	['--font-roboto-italic-ref', robotoItalic],
	['--font-libre-baskerville-italic-ref', libreBaskervilleItalic],
	['--font-inter-italic-ref', interItalic],
] as const;

export const fontFamilyVariables = `html{${fontVars
	.map(([token, font]) => `${token}:${font.style.fontFamily};`)
	.join('')}}`;

export const poppins = Poppins({
	subsets: ['latin'],
	weight: ['400', '600'],
	variable: '--font-poppins',
	display: 'swap',
	preload: false,
});

export const bebasNeue = Bebas_Neue({
	subsets: ['latin'],
	weight: '400',
	variable: '--font-bebas-neue',
	display: 'swap',
	// Condensed, so same problem as Oswald — the metric fallback shrinks ~29% below the real face.
	adjustFontFallback: false,
	fallback: ['Arial Narrow', 'Arial', 'sans-serif'],
	preload: false,
});
